import { motion } from "framer-motion";
import ShareButtons from "../../../utility/ShareButtons";
import FAQAccordion from "../../../utility/FAQAccordion";
import QuickNavigation from "../../../utility/serviceTemplateutility/QuickNavigation";
import CardDesign from "../../../utility/serviceTemplateutility/CardDesign";
import ListDesign from "../../../utility/serviceTemplateutility/ListDesign";
import WhyUs from "../../../utility/serviceTemplateutility/WhyUs";
import MultiStepForm from "../../../utility/serviceTemplateutility/MultiStepForm";
import Testimonials from "../../homepage/Testimonials";
import OurTeam from "../../homepage/OurTeam";
import { useLanguage } from "../../../contextApi/languageContext";
import { useCart } from "../../../contextApi/cartContext";
import { useGlobalData } from "../../../contextApi/globalContextData";
import { maleinfertility } from "../../../text-files/urology/maleinfertility.ts";

const MaleInfertility = () => {
  const serviceUrl = window.location.href;
  const { language } = useLanguage();
  const { addToCart } = useCart();
  const { sectionsData } = useGlobalData();
  const content = maleinfertility[language];
  const shared = maleinfertility.shared;
  const faqData = [
    { heading: "Frequently Asked Questions" },
    {
      question: "Can male infertility be treated successfully?",
      answer:
        "Yes, many causes of male infertility can be treated successfully with medications, surgeries, or assisted reproductive technologies. The success depends on the underlying cause and the chosen treatment.",
    },
    {
      question: "How long does male infertility treatment take?",
      answer:
        "The treatment timeline varies depending on the specific treatment plan. Medications or lifestyle changes may take several months to show effects, while ART procedures like IVF typically follow a structured timeline of a few weeks to months.",
    },
    {
      question: "Is age a factor in male infertility?",
      answer:
        "While male fertility tends to decline with age, men can remain fertile into their 50s and beyond. However, sperm quality may decrease over time, increasing the chances of infertility.",
    },
    {
      question: "Are there any side effects to male infertility treatments?",
      answer:
        "Most treatments, especially non-invasive ones like medications or lifestyle changes, have minimal side effects. However, surgical options and ART procedures may come with risks that should be discussed with your specialist.",
    },
    {
      question: "How can I prevent male infertility?",
      answer:
        "Absolutely. Rehabilitation strengthens the body, improves balance and coordination, and enhances awareness of proper body mechanics, which all contribute to injury prevention.",
    },
  ];

  const infertilityCauses = [
    { heading: "Causes of Male Infertility" },
    {
      para: "Understanding the causes of male infertility is key to identifying the appropriate treatment. The issue can stem from various factors related to sperm production, sperm delivery, or lifestyle factors that affect fertility.",
    },
    {
      title: "Sperm production problems",
      description:
        "Conditions like varicocele, infections, or genetic factors can impair sperm production.",
      icon: "fa-solid fa-syringe", // Example Font Awesome icon class for 'injection'
    },
    {
      title: "Sperm transport issues",
      description:
        "Blockages in the tubes that transport sperm from the testicles can result in infertility.",
      icon: "fa-solid fa-road", // Example Font Awesome icon class for 'road' (transport issues)
    },
    {
      title: "Hormonal imbalances",
      description:
        "Low levels of testosterone or other hormone disorders can negatively impact sperm production.",
      icon: "fa-solid fa-balance-scale", // Example Font Awesome icon class for 'balance scale'
    },
    {
      title: "Lifestyle factors",
      description:
        "Excessive smoking, alcohol, obesity, and exposure to toxins can also contribute to infertility.",
      icon: "fa-solid fa-smoking", // Example Font Awesome icon class for 'smoking'
    },
    {
      title: "Sexual dysfunction",
      description:
        "Erectile dysfunction or ejaculatory problems can impede successful conception.",
      icon: "fa-solid fa-heart-crack", // Example Font Awesome icon class for 'heart crack' (symbolizes dysfunction)
    },
  ];

  const infertilityDiagnosis = [
    { heading: "Diagnosis of Male Infertility" },
    {
      para: "Accurate diagnosis is crucial for determining the cause of infertility and choosing the appropriate treatment. A fertility specialist may perform a variety of tests to assess male reproductive health.",
    },
    {
      title: "Semen analysis",
      description:
        "A common test that evaluates sperm count, motility, and morphology.",
      icon: "fa-solid fa-flask", // Example Font Awesome icon class for 'flask' (lab test)
    },
    {
      title: "Hormone testing",
      description:
        "Blood tests to assess testosterone and other hormone levels.",
      icon: "fa-solid fa-tint", // Example Font Awesome icon class for 'tint' (blood test)
    },
    {
      title: "Genetic tests",
      description:
        "Used to detect genetic abnormalities that may affect fertility.",
      icon: "fa-solid fa-dna", // Font Awesome icon class for 'DNA'
    },
    {
      title: "Testicular biopsy",
      description: "Performed if sperm production issues are suspected.",
      icon: "fa-solid fa-cut", // Font Awesome icon class for 'cut' (biopsy)
    },
    {
      title: "Imaging tests",
      description:
        "Ultrasound can be used to identify blockages or abnormalities in the reproductive organs.",
      icon: "fa-solid fa-image", // Font Awesome icon class for 'image' (ultrasound)
    },
  ];

  const infertilityTreatmentOptions = [
    { heading: "Treatment Options for Male Infertility" },
    {
      para: "Rehabilitation offers numerous physical, mental, and emotional benefits, all of which contribute to an individual’s overall recovery and long-term health.",
    },
    {
      title: "Medications",
      description:
        "Hormonal treatments can correct imbalances, and antibiotics can treat infections affecting fertility.",
      icon: "fa-solid fa-pills", // Font Awesome icon class for 'pills' (medications)
    },
    {
      title: "Lifestyle changes",
      description:
        "Weight loss, quitting smoking, reducing alcohol intake, and stress management can improve fertility.",
      icon: "fa-solid fa-heart", // Font Awesome icon class for 'heart' (lifestyle/health)
    },
    {
      title: "Surgical procedures",
      description:
        "Surgery may be necessary to correct blockages, varicocele, or repair structural abnormalities.",
      icon: "fa-solid fa-scalpel", // Font Awesome icon class for 'scalpel' (surgery)
    },
    {
      title: "Assisted reproductive technologies (ART)",
      description:
        "Intrauterine insemination (IUI) or in vitro fertilization (IVF) may be recommended when natural conception is challenging.",
      icon: "fa-solid fa-robot", // Font Awesome icon class for 'robot' (ART/technology)
    },
    {
      title: "Sperm retrieval techniques",
      description:
        "For men with low sperm counts, sperm can be retrieved directly from the testicles.",
      icon: "fa-solid fa-tint", // Font Awesome icon class for 'tint' (sperm retrieval/medical procedure)
    },
  ];
  const infertilityBenefits = [
    { heading: "Benefits" },
    {
      para: "Treating male infertility can provide significant benefits, improving the chances of conception and addressing related health issues. Early intervention often results in a higher success rate, and many treatments are minimally invasive, offering quick recovery.",
    },
    {
      title: "Improved sperm quality",
      description: "Treatments can increase sperm count and motility.",
      icon: "fa-solid fa-sperm", // Font Awesome icon class for 'sperm' (improved sperm quality)
    },
    {
      title: "Increased chance of conception",
      description:
        "Even with low sperm counts, ART techniques can enhance the likelihood of pregnancy.",
      icon: "fa-solid fa-baby", // Font Awesome icon class for 'baby' (increased conception)
    },
    {
      title: "Enhanced overall health",
      description:
        "Addressing lifestyle factors like weight and smoking not only improves fertility but also boosts general well-being.",
      icon: "fa-solid fa-heartbeat", // Font Awesome icon class for 'heartbeat' (overall health)
    },
    {
      title: "Emotional relief",
      description:
        "Receiving a diagnosis and treatment plan can reduce stress and provide hope for couples.",
      icon: "fa-solid fa-smile", // Font Awesome icon class for 'smile' (emotional relief)
    },
  ];

  const infertilityEligibility = [
    { heading: "Eligibility" },
    {
      para: "Not all treatment options are suitable for every individual. A thorough evaluation by a fertility specialist will determine the most appropriate treatment path based on specific needs.",
    },
    {
      title: "Cause of infertility",
      description:
        "Hormonal, genetic, or physical causes will guide treatment recommendations.",
      icon: "fa-solid fa-dna", // Font Awesome icon class for 'dna' (cause of infertility)
    },
    {
      title: "Age and overall health",
      description:
        "A man's age and underlying health conditions can impact treatment choices.",
      icon: "fa-solid fa-calendar-heart", // Font Awesome icon class for 'calendar-heart' (age and health)
    },
    {
      title: "Duration of infertility",
      description:
        "The length of time the couple has been trying to conceive may influence treatment urgency.",
      icon: "fa-solid fa-clock", // Font Awesome icon class for 'clock' (duration of infertility)
    },
    {
      title: "Partner's fertility health",
      description:
        "In some cases, female fertility issues may also need to be considered in the treatment plan.",
      icon: "fa-solid fa-female", // Font Awesome icon class for 'female' (partner's fertility health)
    },
  ];

  const infertilityPreparation = [
    { heading: "Preparation" },
    {
      para: "Preparing for male infertility treatment involves medical evaluations and making lifestyle changes that support reproductive health. These preparatory steps enhance the success rate of treatments like IUI, IVF, or surgery.",
    },
    {
      title: "Medical history review",
      description:
        "Be ready to discuss any previous illnesses, surgeries, or medications.",
      icon: "fa-solid fa-notes-medical", // Font Awesome icon class for 'notes-medical' (medical history review)
    },
    {
      title: "Semen analysis",
      description: "This is a crucial part of the diagnostic process.",
      icon: "fa-solid fa-flask", // Font Awesome icon class for 'flask' (semen analysis)
    },
    {
      title: "Blood tests",
      description:
        "Check hormone levels to ensure hormonal balance before starting treatment.",
      icon: "fa-solid fa-tint", // Font Awesome icon class for 'tint' (blood tests)
    },
    {
      title: "Healthy lifestyle",
      description:
        "Maintaining a healthy diet, exercising regularly, and avoiding substances that can impact fertility (such as tobacco and alcohol) are essential for increasing treatment success.",
      icon: "fa-solid fa-heart", // Font Awesome icon class for 'heart' (healthy lifestyle)
    },
  ];

  const infertilityResults = [
    { heading: "Results" },
    {
      para: "The success of male infertility treatments varies based on the individual, the cause of infertility, and the chosen method. On average, treatments like IVF have a high success rate, especially when combined with advanced sperm retrieval techniques.",
    },
    {
      title: "Improved sperm count",
      description:
        "Medications and lifestyle changes can lead to higher sperm counts and better sperm quality.",
      icon: "fa-solid fa-sperm", // Font Awesome icon class for 'sperm' (improved sperm count)
    },
    {
      title: "Higher conception rates",
      description:
        "With ART techniques, even men with low sperm counts can achieve successful conception.",
      icon: "fa-solid fa-baby", // Font Awesome icon class for 'baby' (higher conception rates)
    },
    {
      title: "Healthier lifestyle",
      description:
        "Addressing underlying health issues or making positive lifestyle changes can improve overall well-being and fertility.",
      icon: "fa-solid fa-heartbeat", // Font Awesome icon class for 'heartbeat' (healthier lifestyle)
    },
  ];

  return (
    <>
      <title>Male Infertility Treatment Dubai -</title>
      <meta
        name="description"
        content="Male infertility refers to a man&#039;s inability to impregnate a female partner after trying for at least a year of unprotected intercourse. It accounts for about"
      />
      <meta
        name="robots"
        content="follow, index, max-snippet:-1, max-video-preview:-1, max-image-preview:large"
      />
      <link
        rel="canonical"
        href="https://elitebodyhome.com/services/services/male-infertility-treatment-dubai/"
      />
      <meta property="og:locale" content="en_US" />
      <meta property="og:type" content="article" />
      <meta property="og:title" content="Male Infertility Treatment Dubai -" />
      <meta
        property="og:description"
        content="Male infertility refers to a man&#039;s inability to impregnate a female partner after trying for at least a year of unprotected intercourse. It accounts for about"
      />
      <meta
        property="og:url"
        content="https://elitebodyhome.com/services/male-infertility-treatment-dubai/"
      />
      <meta property="og:site_name" content="elitebodyhome.com" />
      <meta property="og:updated_time" content="2025-01-23T09:35:48+00:00" />
      <meta
        property="og:image"
        content="https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/Elite-Body-Home-Services-for-Astatic-Clinic-in-Dubai-8.jpg"
      />
      <meta
        property="og:image:secure_url"
        content="https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/Elite-Body-Home-Services-for-Astatic-Clinic-in-Dubai-8.jpg"
      />
      <meta property="og:image:width" content="1254" />
      <meta property="og:image:height" content="700" />
      <meta
        property="og:image:alt"
        content="Male Infertility Treatment Dubai"
      />
      <meta property="og:image:type" content="image/jpeg" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content="Male Infertility Treatment Dubai -" />
      <meta
        name="twitter:description"
        content="Male infertility refers to a man&#039;s inability to impregnate a female partner after trying for at least a year of unprotected intercourse. It accounts for about"
      />
      <meta
        name="twitter:image"
        content="https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/Elite-Body-Home-Services-for-Astatic-Clinic-in-Dubai-8.jpg"
      />
      <div
        className="min-vh-100 font-sans text-dark  mb-5 mx-3"
        dir={language === "ar" ? "rtl" : "ltr"}
      >
        {/* Hero Image Section */}
        {shared.images.main && (
          <section className="overflow-hidden ">
            <div className="position-relative mb-3">
              <img
                src={shared.images.main}
                alt="male infertility Dubai"
                className="img-fluid w-100 rounded mt-2 "
                style={{
                  height: "500px",
                  objectFit: "cover",
                  objectPosition: "top",
                  border: "2px solid #c69933",
                  borderRadius: "100px",
                }}
              />
            </div>
          </section>
        )}

        <div className="d-flex gap-4  w-100 ">
          <div
            className=" rounded-4 p-1 bg-white flex-grow-1 "
            style={{
              width: "65%",
              border: "2px solid #c69933",
            }}
          >
            {/* Main Content Column */}

            {/* Breadcrumbs */}
            <nav aria-label="breadcrumb" className="p-2">
              <ol className="breadcrumb">
                {content.breadcrumbs.map((item, index) => (
                  <li
                    key={index}
                    className={`breadcrumb-item ${
                      index === content.breadcrumbs.length - 1 ? "active" : ""
                    }`}
                    aria-current={
                      index === content.breadcrumbs.length - 1
                        ? "page"
                        : undefined
                    }
                  >
                    {index === content.breadcrumbs.length - 1 ? (
                      item
                    ) : (
                      <a href={index === 0 ? "/home" : "#"}>{item}</a>
                    )}
                  </li>
                ))}
              </ol>
            </nav>

            {/* Title Section */}
            <div className="text-center my-2">
              <motion.h1
                className="display-4 fw-bold"
                style={{ color: "#c69933" }}
              >
                {content.hero.title}
              </motion.h1>

              {content.hero.subtitle && (
                <motion.h2 className="fw-semibold mt-3">
                  {content.hero.subtitle}
                </motion.h2>
              )}

              {content.hero.description && (
                <motion.p className="lead mt-3">
                  {content.hero.description}
                </motion.p>
              )}
            </div>

            <QuickNavigation />

            {/* How It Works Section */}

            {/* Introduction Image */}
            {shared.images.introductionImage && (
              <motion.div className="my-5">
                <div className="text-center">
                  <img
                    src={shared.images.introductionImage}
                    alt="fat freezing treatment dubai"
                    className="img-fluid rounded shadow"
                    style={{ maxHeight: "500px" }}
                    loading="lazy"
                    decoding="async"
                  />
                </div>
              </motion.div>
            )}

            <section id="benefits" className="my-5">
              <ListDesign listitems={content.causes} />
            </section>

            {/* Process Section */}
            <section id="process" className="my-5">
              <CardDesign carditems={content.procedureSteps} />
            </section>

            <section id="benefits" className="my-5">
              <ListDesign listitems={content.diagnosis} />
            </section>

            {/* Benefits Section */}
            <section id="benefits" className="my-5">
              <ListDesign listitems={content.benefits} />
            </section>

            <section id="process" className="my-5">
              <ListDesign listitems={content.treatmentOptions} />
            </section>

            {/* Eligibility Section */}
            <section id="eligibility" className="my-5">
              <CardDesign listitems={content.eligibility} />
            </section>

            {/* Preparation Section */}
            <section id="preparations" className="my-5">
              <ListDesign listitems={content.preparationTips} />
            </section>

            {/* Results Section */}
            <section id="results" className="my-5">
              <ListDesign listitems={content.results} />
            </section>

            {/* Before & After Section */}
            {/* {shared.images.beforeAfter &&
              shared.images.beforeAfter.length > 0 && (
                <motion.section
                  className="my-5"
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                >
                  <BeforeAfterCarousel
                    language={language}
                    images={shared.images.beforeAfter}
                  />
                </motion.section>
              )} */}
            {/* Aftercare Section */}
            <section className="my-5">
              <ListDesign listitems={content.aftercareTips} />
            </section>

            {/* Product Card */}
            {shared.images.main && (
              <motion.div
                className="my-5"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
              >
                <div
                  className="card border-0 shadow-lg rounded-4 overflow-hidden mx-auto"
                  style={{ maxWidth: "420px" }}
                >
                  <img
                    src={shared.images.main}
                    alt={content.meta.title}
                    className="card-img-top"
                    style={{ height: "280px", objectFit: "cover" }}
                  />
                  <div className="card-body text-center px-4 py-4">
                    <h5 className="card-title fw-bold text-uppercase text-dark mb-2">
                      {content.meta.title}
                    </h5>
                    <p className="card-text text-muted small">
                      {content.meta.description}
                    </p>
                    {/* <h6
                        className="text-success fw-bold mt-3"
                        style={{ fontSize: "1.1rem" }}
                      >
                        {language === "en" ? "Price" : "السعر"}: AED{" "}
                        {shared.product.price}
                      </h6> */}
                    <button
                      className="btn rounded-pill px-4 py-2 mt-3 text-white fw-bold"
                      style={{
                        background: "#c69933",
                        border: "2px solid #c69933",
                      }}
                      onClick={() =>
                        addToCart({
                          ...shared.product,
                          image: shared.images.main,
                        })
                      }
                    >
                      {language === "en" ? "Add to Cart" : "أضف إلى السلة"}
                    </button>
                  </div>
                </div>
              </motion.div>
            )}
            <WhyUs language={language} />
            <OurTeam data={sectionsData["Professional"]} />
            <Testimonials
              data={sectionsData["Success Stories"]}
              language={language}
            />
            <FAQAccordion faqData={content.faqData} />
            <ShareButtons url={serviceUrl} />
          </div>
          <MultiStepForm />
        </div>
      </div>
    </>
  );
};

export default MaleInfertility;
